import React, { useCallback, useEffect, useMemo, useState } from 'react'
import * as walletService from '../../services/walletService'
import {
  getMyAmbTransactions,
  type AmbHistoryMode,
  type AmbHistoryRoundEvent,
  type AmbHistoryRoundSummary,
} from '../../services/ambHistoryService'
import { HistoryCollapseCard, type HistoryCardLeftIcon, type HistoryDetailRow } from './HistoryCollapseCard'

const PAGE_SIZE = 20

type ReportMode = Exclude<AmbHistoryMode, 'all'>
type DatePreset = 'all' | 'today' | '7d' | '30d'

const SETTLED_STATUS_OPTIONS = [
  { value: 'all', label: 'ทั้งหมด' },
  { value: 'SETTLED', label: 'จบแล้ว' },
  { value: 'COMPLETED', label: 'สำเร็จ' },
  { value: 'REFUND', label: 'คืนเงิน' },
  { value: 'VOID', label: 'ยกเลิก/โมฆะ' },
  { value: 'CANCELLED', label: 'ยกเลิก' },
]

const PENDING_STATUS_OPTIONS = [
  { value: 'all', label: 'ทั้งหมด' },
  { value: 'OPEN', label: 'กำลังเล่น' },
  { value: 'WAITING', label: 'รอผล' },
]

const STATUS_LABEL: Record<string, string> = {
  WAITING: 'รอผล',
  OPEN: 'กำลังเล่น',
  SETTLED: 'จบแล้ว',
  COMPLETED: 'สำเร็จ',
  REFUND: 'คืนเงิน',
  REJECT: 'ไม่สำเร็จ',
  VOID: 'โมฆะ',
  CANCELLED: 'ยกเลิก',
}

const TRANSACTION_TYPE_LABEL: Record<string, string> = {
  PLACE_BET: 'วางเดิมพัน',
  SETTLE_BET: 'สรุปผลเดิมพัน',
  CANCEL_BET: 'ยกเลิกเดิมพัน',
  ADJUST_BET: 'ปรับยอดเดิมพัน',
  ROLLBACK: 'ย้อนรายการ',
  WIN_REWARD: 'รับรางวัล',
  PLACE_TIP: 'เดิมพันพิเศษ',
  CANCEL_TIP: 'ยกเลิกเดิมพันพิเศษ',
  VOID_SETTLED: 'โมฆะผลเดิมพัน',
  ADJUST_BALANCE: 'ปรับยอด',
  CHECK_BALANCE: 'ตรวจสอบยอด',
  SETTLE_SINGLE_STATE: 'สรุปผลเกม',
}

const PRESET_LABELS: Record<DatePreset, string> = {
  all: 'ทั้งหมด',
  today: 'วันนี้',
  '7d': '7 วัน',
  '30d': '30 วัน',
}

function getPresetDescription(preset: DatePreset): string {
  const range = getPresetRange(preset)
  if (preset === 'all') return 'ทุกช่วงเวลา'
  if (preset === 'today') return `วันนี้ (${range.startDate})`
  return `${PRESET_LABELS[preset]} (${range.startDate} ถึง ${range.endDate})`
}

function formatBangkokDate(date: Date): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Bangkok',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date)
  const year = parts.find((part) => part.type === 'year')?.value || '1970'
  const month = parts.find((part) => part.type === 'month')?.value || '01'
  const day = parts.find((part) => part.type === 'day')?.value || '01'
  return `${year}-${month}-${day}`
}

function shiftBangkokDate(dateString: string, days: number): string {
  const date = new Date(`${dateString}T00:00:00+07:00`)
  date.setUTCDate(date.getUTCDate() + days)
  return formatBangkokDate(date)
}

function toBangkokApiDate(dateString: string, boundary: 'start' | 'end'): string {
  if (!dateString) return ''
  return boundary === 'start'
    ? `${dateString}T00:00:00.000+07:00`
    : `${dateString}T23:59:59.999+07:00`
}

function getPresetRange(preset: DatePreset): { startDate: string; endDate: string } {
  if (preset === 'all') {
    return { startDate: '', endDate: '' }
  }

  const todayBangkok = formatBangkokDate(new Date())

  if (preset === 'today') {
    return { startDate: todayBangkok, endDate: todayBangkok }
  }

  return {
    startDate: shiftBangkokDate(todayBangkok, preset === '7d' ? -6 : -29),
    endDate: todayBangkok,
  }
}

function formatDateTime(dateStr?: string | null): string {
  if (!dateStr) return '-'
  try {
    return new Date(dateStr).toLocaleString('th-TH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dateStr
  }
}

function formatMoney(value?: number | null): string {
  return walletService.formatCurrency(Number(value) || 0)
}

function getStatusLabel(status?: string): string {
  return STATUS_LABEL[String(status || '').toUpperCase()] || String(status || '-')
}

function getTransactionTypeLabel(type?: string): string {
  return TRANSACTION_TYPE_LABEL[String(type || '').toUpperCase()] || String(type || '-')
}

function getPrimaryTimestamp(summary: AmbHistoryRoundSummary, mode: ReportMode): string {
  if (mode === 'pending') {
    return formatDateTime(summary.placedAt || summary.createdAt)
  }
  return formatDateTime(summary.settledAt || summary.updatedAt || summary.createdAt)
}

function getLeftIcon(summary: AmbHistoryRoundSummary, mode: ReportMode): HistoryCardLeftIcon {
  const status = String(summary.status || '').toUpperCase()
  const netAmount = Number(summary.netAmount) || 0

  if (mode === 'pending' || status === 'WAITING' || status === 'OPEN') return 'pending'
  if (status === 'REFUND' || status === 'VOID' || status === 'CANCELLED') return 'info'
  if (netAmount > 0 || status === 'SETTLED' || status === 'COMPLETED') return 'success'
  return 'reject'
}

function buildSearchText(summary: AmbHistoryRoundSummary): string {
  return [
    summary.transactionId,
    summary.betId,
    summary.roundId,
    summary.productId,
    summary.gameCode,
    summary.gameName,
    getStatusLabel(summary.status),
    formatDateTime(summary.createdAt),
    ...(summary.transactionIds || []),
    ...(summary.events || []).map((event) => `${event.transactionType} ${event.status}`),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
}

function summarizeEvents(events: AmbHistoryRoundEvent[] = []): string {
  if (!Array.isArray(events) || events.length === 0) return '-'

  const lines = events.slice(0, 4).map((event, index) => {
    const typeLabel = getTransactionTypeLabel(event.transactionType)
    const win = Number(event.winAmount) || 0
    const bet = Number(event.amount) || 0
    const signedNet = Number(event.netAmount) || 0
    return `${index + 1}. ${typeLabel}\n   เดิมพัน ${formatMoney(bet)} · ชนะ ${formatMoney(win)} · สุทธิ ${signedNet > 0 ? '+' : ''}${formatMoney(signedNet)}`
  })

  const body = lines.join('\n')
  return events.length > 4 ? `${body}\n…และอีก ${events.length - 4} รายการ` : body
}

function getRoundHint(summary: AmbHistoryRoundSummary): string {
  if ((summary.zeroBetWinEventCount || 0) > 0 && (summary.amount || 0) > 0) {
    return `รอบนี้มีโบนัส/ผลย่อย ${summary.zeroBetWinEventCount} รายการ โดยยอดเดิมพันหลักถูกหักไปแล้วในรายการก่อนหน้า`
  }
  if ((summary.eventCount || 0) > 1) {
    return `รอบนี้มี ${summary.eventCount} เหตุการณ์จากเกมเดียวกัน จึงถูกรวมให้เหลือ 1 รายการ`
  }
  return '1 รายการนี้คือสรุปของ 1 รอบการเล่น'
}

function getRoundResultText(netAmount?: number | null): string {
  const value = Number(netAmount) || 0
  if (value > 0) {
    return `กำไรในรอบนี้ ${formatMoney(value)} บาท`
  }
  if (value < 0) {
    return `ขาดทุนในรอบนี้ ${formatMoney(Math.abs(value))} บาท`
  }
  return 'ผลลัพธ์รอบนี้ 0.00 บาท'
}

interface HistoryAmbReportProps {
  mode: ReportMode
}

const HistoryAmbReport: React.FC<HistoryAmbReportProps> = ({ mode }) => {
  const [roundSummaries, setRoundSummaries] = useState<AmbHistoryRoundSummary[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [datePreset, setDatePreset] = useState<DatePreset>('all')

  const { startDate, endDate } = useMemo(() => getPresetRange(datePreset), [datePreset])

  const title = mode === 'pending' ? 'รอผลการเล่นพนัน AMB' : 'ประวัติการเล่นพนัน AMB'
  const emptyText = mode === 'pending' ? 'ยังไม่มีรายการ AMB ที่รอผล' : 'ยังไม่มีประวัติการเล่น AMB'
  const statusOptions = mode === 'pending' ? PENDING_STATUS_OPTIONS : SETTLED_STATUS_OPTIONS

  const loadTransactions = useCallback(async (pageNum: number = 1) => {
    try {
      setLoading(true)
      setError(null)
      const result = await getMyAmbTransactions({
        mode,
        page: pageNum,
        limit: PAGE_SIZE,
        ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
        ...(startDate ? { startDate: toBangkokApiDate(startDate, 'start') } : {}),
        ...(endDate ? { endDate: toBangkokApiDate(endDate, 'end') } : {}),
      })

      setRoundSummaries(result.roundSummaries || [])
      setTotal(result.pagination?.total || 0)
      setPage(result.pagination?.page || pageNum)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'โหลดข้อมูลไม่สำเร็จ')
      setRoundSummaries([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [endDate, mode, startDate, statusFilter])

  useEffect(() => {
    loadTransactions(1)
  }, [loadTransactions])

  const filteredTransactions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return roundSummaries
    return roundSummaries.filter((summary) => buildSearchText(summary).includes(q))
  }, [searchQuery, roundSummaries])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const handlePresetChange = (preset: DatePreset) => {
    setDatePreset(preset)
  }

  const handleClearFilters = () => {
    setSearchQuery('')
    setStatusFilter('all')
    setDatePreset('all')
  }

  const handleCopyRef = async (refId: string) => {
    try {
      await navigator.clipboard.writeText(refId)
    } catch {
      // ignore clipboard failure
    }
  }

  return (
    <div className="htd-hist__stack">
      <h2 className="htd-hist__page-title">{title}</h2>

      <div className="htd-hist__panel">
        <div className="htd-hist__search">
          <span className="htd-hist__search-icon" aria-hidden>
            <i className="fas fa-search" />
          </span>
          <input
            type="text"
            placeholder="ค้นหาเกม, ผู้ให้บริการ, สถานะ, รหัสรายการ..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery.trim() && (
            <button
              type="button"
              className="htd-hist__search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="ล้างคำค้นหา"
            >
              <i className="fas fa-times" />
            </button>
          )}
        </div>
        <span className="htd-hist__hint">ค้นหาในรายการ AMB ที่โหลดอยู่ของหน้าปัจจุบัน</span>

        <div className="htd-hist__chips mt-3">
          {(['all', 'today', '7d', '30d'] as DatePreset[]).map((preset) => (
            <button
              key={preset}
              type="button"
              className={`htd-hist__chip${datePreset === preset ? ' is-active is-active--green' : ''}`}
              onClick={() => handlePresetChange(preset)}
            >
              {PRESET_LABELS[preset]}
            </button>
          ))}
        </div>
        <span className="htd-hist__hint">
          ช่วงเวลา: {getPresetDescription(datePreset)} · เลือกได้จากปุ่มด้านบนเท่านั้น
        </span>

        <div className="htd-hist__chips mt-3">
          {statusOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`htd-hist__chip${statusFilter === option.value ? ' is-active' : ''}`}
              onClick={() => setStatusFilter(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="htd-hist__actions mt-3">
          <button type="button" className="htd-hist__btn" onClick={() => loadTransactions(1)}>
            <i className="fas fa-filter" /> ใช้ตัวกรอง
          </button>
          <button type="button" className="htd-hist__btn htd-hist__btn--ghost" onClick={handleClearFilters}>
            ล้างตัวกรอง
          </button>
        </div>
      </div>

      <div className="htd-hist__panel">
        <div className="htd-hist__summary">
          <div>
            <strong>รายการที่แสดงในหน้านี้</strong>
            <span>แสดงผลจากรายการที่โหลดอยู่ในหน้าปัจจุบัน และช่องค้นหาจะกรองเฉพาะรายการชุดนี้</span>
          </div>
          <div className="text-end">
            <strong>{filteredTransactions.length} รายการ</strong>
            <span>ทั้งหมด {total} รายการ</span>
          </div>
        </div>
      </div>

      <div className="htd-hist__list">
        {loading && (
          <div className="htd-hist__loading">
            <h4>
              <i className="fas fa-spinner fa-spin me-2" />
              กำลังโหลดประวัติการเล่น AMB...
            </h4>
          </div>
        )}

        {!loading && error && (
          <div className="htd-hist__shell">
            <h4>{error}</h4>
          </div>
        )}

        {!loading && !error && filteredTransactions.length === 0 && (
          <div className="htd-hist__empty">
            <h4>{emptyText}</h4>
          </div>
        )}

        {!loading && !error && filteredTransactions.map((transaction) => {
          const refId = transaction.roundId || transaction.transactionId || transaction._id
          const gameLabel = transaction.gameName || transaction.gameCode || '-'
          const providerLabel = transaction.productId || '-'
          const primaryTime = getPrimaryTimestamp(transaction, mode)
          const statusLabel = getStatusLabel(transaction.status)
          const netAmount = Number(transaction.netAmount) || 0
          const netTone: HistoryDetailRow['tone'] = netAmount > 0 ? 'positive' : netAmount < 0 ? 'negative' : 'default'
          const rows: HistoryDetailRow[] = [
            { label: 'สถานะ', value: statusLabel },
            { label: 'ประเภทรายการ', value: 'สรุปต่อ 1 รอบการเล่น' },
            { label: 'ผู้ให้บริการ', value: providerLabel },
            { label: 'เกม', value: gameLabel },
            { label: 'จำนวนเหตุการณ์ในรอบ', value: String(transaction.eventCount || 0) },
            { label: 'ยอดเดิมพันรวม', value: `${formatMoney(transaction.amount)} ${transaction.currency || 'THB'}` },
            { label: 'ยอดรับรวม', value: `${formatMoney(transaction.winAmount)} ${transaction.currency || 'THB'}` },
            {
              label: 'สุทธิรวม',
              value: `${formatMoney(transaction.netAmount)} ${transaction.currency || 'THB'}`,
              tone: netTone,
            },
            { label: 'กำไร/ขาดทุนในรอบ', value: getRoundResultText(transaction.netAmount), tone: netTone },
            { label: 'ยอดเงินก่อน', value: `${formatMoney(transaction.balanceBefore)} ${transaction.currency || 'THB'}` },
            { label: 'ยอดเงินหลัง', value: `${formatMoney(transaction.balanceAfter)} ${transaction.currency || 'THB'}` },
            { label: 'เวลาเริ่ม', value: formatDateTime(transaction.placedAt || transaction.createdAt) },
            { label: 'เวลาปิดรายการ', value: formatDateTime(transaction.settledAt || transaction.updatedAt) },
            { label: 'คำอธิบาย', value: getRoundHint(transaction), tone: 'muted' },
            { label: 'รายการย่อยในรอบ', value: summarizeEvents(transaction.events), tone: 'muted' },
            { label: 'Transaction หลัก', value: transaction.transactionId || '-', tone: 'muted' },
            { label: 'Bet ID หลัก', value: transaction.betId || '-', tone: 'muted' },
            { label: 'Round ID', value: transaction.roundId || '-', tone: 'muted' },
            { label: 'Game Code', value: transaction.gameCode || '-', tone: 'muted' },
          ]

          return (
            <HistoryCollapseCard
              key={transaction._id || refId}
              id={transaction._id || refId}
              leftIcon={getLeftIcon(transaction, mode)}
              refId={refId}
              onCopyRef={() => handleCopyRef(refId)}
              header={(
                <div className="htd-hist-amb-header">
                  <div className="htd-hist-amb-header__main">
                    <strong>{gameLabel}</strong>
                    <small>
                      {providerLabel} · เดิมพันรวม {formatMoney(transaction.amount)} · {transaction.eventCount} เหตุการณ์
                    </small>
                    <small>
                      {statusLabel} · ยอดก่อน {formatMoney(transaction.balanceBefore)} → หลัง {formatMoney(transaction.balanceAfter)}
                    </small>
                  </div>
                  <div className="htd-hist-amb-header__meta">
                    <strong>{primaryTime}</strong>
                    <small className={netAmount >= 0 ? 'is-gain' : 'is-loss'}>
                      สุทธิ {formatMoney(transaction.netAmount)}
                    </small>
                  </div>
                </div>
              )}
              rows={rows}
            />
          )
        })}
      </div>

      {!loading && !error && totalPages > 1 && (
        <div className="htd-hist__pager">
          <button
            type="button"
            className="htd-hist__btn htd-hist__btn--ghost"
            disabled={page <= 1}
            onClick={() => loadTransactions(page - 1)}
          >
            <i className="fas fa-chevron-left" />
            ก่อนหน้า
          </button>
          <span>
            หน้า {page} / {totalPages}
          </span>
          <button
            type="button"
            className="htd-hist__btn htd-hist__btn--ghost"
            disabled={page >= totalPages}
            onClick={() => loadTransactions(page + 1)}
          >
            ถัดไป
            <i className="fas fa-chevron-right" />
          </button>
        </div>
      )}
    </div>
  )
}

export default HistoryAmbReport
